import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import flyerService, { FLYER_FORMATS, Flyer, FlyerFormat } from '../services/flyerService';
import { PageHeader } from '../components/ui';
import { routeById } from '../navigation/routes';
import { useBerechtigung } from '../berechtigungen/useBerechtigung';
import { rollenAufzaehlung, rollenMitRecht } from '../verwaltung/rollen';

const FORMAT_LABELS: Record<FlyerFormat, string> = {
  pdf: 'PDF',
  linkedin: 'LinkedIn',
  square: 'Square',
  story: 'Story',
};

const STATUS_CLASSES: Record<Flyer['status'], string> = {
  draft: 'badge',
  ready: 'badge badge-success',
  posted: 'badge badge-primary',
  failed: 'badge badge-error',
};

const FlyersPage: React.FC = () => {
  // POST /flyers/:id/post-linkedin veroeffentlicht direkt auf LinkedIn und
  // verlangt linkedin.freigeben (Welle 2); Mitarbeit laedt nur herunter.
  const { darf } = useBerechtigung();
  const darfFreigeben = darf('linkedin.freigeben');
  const [flyers, setFlyers] = useState<Flyer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [posting, setPosting] = useState<string | null>(null);
  // ?postIdeaId=: nur die Flyer einer Idee (Sprung aus den Beitragsideen).
  const [searchParams, setSearchParams] = useSearchParams();
  const ideeFilter = searchParams.get('postIdeaId');
  const sichtbar = ideeFilter ? flyers.filter((f) => f.postIdeaId === ideeFilter) : flyers;

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await flyerService.listFlyers();
      setFlyers(data);
    } catch (err: any) {
      setError(err?.response?.data?.error ?? err?.message ?? 'Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const hasFormat = (flyer: Flyer, format: FlyerFormat): boolean => {
    switch (format) {
      case 'pdf':
        return !!flyer.pdfPath;
      case 'linkedin':
        return !!flyer.linkedinImagePath;
      case 'square':
        return !!flyer.instaSquarePath;
      case 'story':
        return !!flyer.instaStoryPath;
      default:
        return false;
    }
  };

  const handleDownload = async (flyer: Flyer, format: FlyerFormat) => {
    try {
      await flyerService.triggerDownload(flyer.id, format);
    } catch (err: any) {
      setError(err?.response?.data?.error ?? err?.message ?? 'Download fehlgeschlagen');
    }
  };

  const handlePostLinkedIn = async (flyer: Flyer) => {
    try {
      setPosting(flyer.id);
      const updated = await flyerService.postToLinkedIn(flyer.id);
      setFlyers((prev) => prev.map((f) => (f.id === flyer.id ? updated : f)));
    } catch (err: any) {
      setError(err?.response?.data?.error ?? err?.message ?? 'LinkedIn-Post fehlgeschlagen');
    } finally {
      setPosting(null);
    }
  };

  return (
    <div className="page-container" style={{ padding: 24 }}>
      <PageHeader title="Flyer" subtitle={routeById('li-flyers')?.description} />

      {error && <div className="alert alert-error mb-4">{error}</div>}

      {!darfFreigeben && (
        <p className="text-sm text-muted" data-testid="rechte-hinweis">
          Auf LinkedIn veröffentlichen ist {rollenAufzaehlung(rollenMitRecht('linkedin.freigeben'))} vorbehalten.
        </p>
      )}

      {loading ? (
        <p className="text-muted">Wird geladen…</p>
      ) : flyers.length === 0 ? (
        <p className="text-muted">Noch keine Flyer erzeugt.</p>
      ) : (
        <>
        {ideeFilter && (
          <p className="lead-kampagnen-filter">
            Nur Flyer zu <Link to={`/post-ideas?id=${encodeURIComponent(ideeFilter)}`}>{sichtbar[0]?.postIdea?.title ?? 'dieser Idee'}</Link>
            <button type="button" className="btn btn-sm btn-ghost" aria-label="Ideenfilter entfernen" onClick={() => setSearchParams({}, { replace: true })}>×</button>
          </p>
        )}
        <table className="table" style={{ width: '100%', marginTop: 16 }}>
          <thead>
            <tr>
              <th>Datum</th>
              <th>Serie</th>
              <th>Titel</th>
              <th>Status</th>
              <th>Downloads</th>
              <th>LinkedIn</th>
            </tr>
          </thead>
          <tbody>
            {sichtbar.map((flyer) => (
              <tr key={flyer.id}>
                <td>{new Date(flyer.createdAt).toLocaleString()}</td>
                <td>{flyer.seriesFormat?.name ?? '—'}</td>
                <td>
                  <Link to={`/post-ideas?id=${encodeURIComponent(flyer.postIdeaId)}`} title="Beitragsidee öffnen">
                    {flyer.postIdea?.title ?? 'Beitragsidee'}
                  </Link>
                </td>
                <td>
                  <span className={STATUS_CLASSES[flyer.status]}>{flyer.status}</span>
                </td>
                <td>
                  <div className="flex gap-2" style={{ flexWrap: 'wrap' }}>
                    {FLYER_FORMATS.map((format) => (
                      <button
                        key={format}
                        className="btn btn-sm btn-secondary"
                        disabled={!hasFormat(flyer, format)}
                        onClick={() => handleDownload(flyer, format)}
                      >
                        {FORMAT_LABELS[format]}
                      </button>
                    ))}
                  </div>
                </td>
                <td>
                  {flyer.linkedinPostUrl ? (
                    <a href={flyer.linkedinPostUrl} target="_blank" rel="noreferrer">
                      Ansehen
                    </a>
                  ) : !darfFreigeben ? (
                    <span className="text-muted">—</span>
                  ) : (
                    <button
                      className="btn btn-sm btn-primary"
                      disabled={
                        posting === flyer.id ||
                        !flyer.linkedinImagePath ||
                        flyer.status === 'posted'
                      }
                      onClick={() => handlePostLinkedIn(flyer)}
                    >
                      {posting === flyer.id ? 'Poste…' : 'Posten'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </>
      )}
    </div>
  );
};

export default FlyersPage;
